import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, clearApiCache } from "./api";
import { getRefreshToken } from "./session";
import { portalFor, getToken, setToken, clearToken, keyFor } from "./session";

const AuthCtx = createContext(null);

function PortalAuth({ portal, children }) {
  const [user, setUser] = useState(null);
  const [institute, setInstitute] = useState(null);
  const [planFeatures, setPlanFeatures] = useState(null);
  const [account, setAccount] = useState(null); // plan_limits, usage, read_only, payment_failed_at, legacy_plan, scheduled_plan
  const [effectivePlan, setEffectivePlan] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    if (!getToken(portal)) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const r = await api.get("/auth/me", { portal });
      setUser(r.data.user);
      setInstitute(r.data.institute);
      setPlanFeatures(r.data.plan_features);
      setEffectivePlan(r.data.effective_plan);
      setAccount({ plan_limits: r.data.plan_limits, usage: r.data.usage, read_only: r.data.read_only, payment_failed_at: r.data.payment_failed_at, legacy_plan: r.data.legacy_plan, scheduled_plan: r.data.scheduled_plan });
    } catch (err) {
      if (!err.__sessionExpired) clearToken(portal);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // Another tab logged out (or re-logged) this portal: mirror it here only if it was our session.
    const onStorage = (e) => {
      if (e.key !== keyFor(portal)) return;
      const mine = sessionStorage.getItem(keyFor(portal));
      if (e.newValue === null && (!mine || mine === e.oldValue)) {
        sessionStorage.removeItem(keyFor(portal));
        setUser(null);
        window.location.assign(portal === "teacher" ? "/teacher-login" : "/");
      } else if (e.newValue && !mine) {
        refresh();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portal]);

  const login = async (email, password, kind = "owner") => {
    const target = kind === "teacher" ? "teacher" : "owner";
    const r = await api.post(target === "teacher" ? "/teacher/login" : "/auth/login", { email, password }, { portal: target });
    clearApiCache();
    setToken(target, r.data.token, r.data.refresh_token);
    if (target === portal) await refresh();
    return r.data;
  };

  const adoptToken = async (token, target = "owner", refreshToken = null) => {
    clearApiCache();
    setToken(target, token, refreshToken);
    if (portal === target) await refresh();
  };

  const logout = () => {
    const rt = getRefreshToken(portal);
    if (rt) api.post("/auth/logout", { refresh_token: rt }, { portal }).catch(() => {});
    clearApiCache();
    clearToken(portal);
    setUser(null);
    setInstitute(null);
    window.location.href = portal === "teacher" ? "/teacher-login" : "/";
  };

  return (
    <AuthCtx.Provider value={{ portal, user, institute, planFeatures, effectivePlan, account, loading, login, logout, refresh, setInstitute, adoptToken }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function AuthProvider({ children }) {
  const portal = portalFor(useLocation().pathname);
  return <PortalAuth key={portal} portal={portal}>{children}</PortalAuth>;
}

export const useAuth = () => useContext(AuthCtx);
